import type { IncomingView, Order } from '@nebueye/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { ProductThumb, RetailerMark } from '@/components/app/retailer'
import { EmptyState, ErrorState, OrbitArt } from '@/components/app/states'
import { StatusBadge } from '@/components/app/status-badge'
import { ColumnHeader, GroupLabel, PageTitle, RowChevron, StatBar, Surface, rowClass, rowEnter } from '@/components/app/surface'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { trackingUrl } from '@/lib/carriers'
import { arrivalLabel, count } from '@/lib/format'
import { incomingQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/incoming')({
  loader: ({ context }) => context.queryClient.prefetchQuery(incomingQuery()),
  head: () => ({ meta: [{ title: 'Incoming · Nebueye' }] }),
  component: Incoming,
})

/** item · ship to · status · tracking · chevron */
const COLS = 'grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_15rem_11rem_12rem_1.25rem]'

const todayKey = () => new Date().toLocaleDateString('en-CA')

function Incoming() {
  const q = useQuery(incomingQuery())
  const d = q.data
  const upcoming = d?.upcoming.reduce((s, g) => s + g.orders.length, 0) ?? 0
  const expectedToday = d?.upcoming.find((g) => g.date === todayKey())?.orders.length ?? 0

  return (
    <div className="space-y-6">
      <PageTitle title="Incoming" description="Everything that has shipped, grouped by the day it should arrive." />

      {d ? (
        <StatBar
          stats={[
            { label: 'Out for delivery', value: count(d.outForDelivery.length), sub: 'On a truck right now' },
            { label: 'Expected today', value: count(expectedToday), sub: 'By the carrier estimate' },
            { label: 'On the way', value: count(upcoming), sub: 'Shipped, not delivered' },
            { label: 'Delivered today', value: count(d.deliveredToday.length), sub: 'Arrived so far today' },
          ]}
        />
      ) : (
        !q.isError && <Skeleton className="h-[88px] w-full rounded-xl" />
      )}

      <Surface aria-label="Incoming packages">
        <ColumnHeader cols={COLS}>
          <span>Item</span>
          <span>Ship to</span>
          <span>Status</span>
          <span>Tracking</span>
          <span />
        </ColumnHeader>
        {q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} className="px-6" />
        ) : !d ? (
          <div aria-busy="true" aria-label="Loading packages">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="flex items-center gap-3.5 border-b px-5 py-3 last:border-b-0">
                <Skeleton className="size-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-2/5" />
                  <Skeleton className="h-3 w-40" />
                </div>
                <Skeleton className="h-6 w-24 rounded-full" />
              </div>
            ))}
          </div>
        ) : d.outForDelivery.length + d.deliveredToday.length + upcoming === 0 ? (
          <EmptyState className="px-6" art={<OrbitArt />} title="Nothing on the way">
            Shipped orders show up here, grouped by the day they're expected, until they're delivered.
          </EmptyState>
        ) : (
          <Groups d={d} />
        )}
      </Surface>
    </div>
  )
}

function expectedTitle(date: string) {
  const label = arrivalLabel(date)
  return `Expected ${label === 'Today' || label === 'Tomorrow' ? label.toLowerCase() : label}`
}

function Groups({ d }: { d: IncomingView }) {
  const groups: { key: string; title: string; dot?: string; orders: Order[] }[] = [
    { key: 'ofd', title: 'Out for delivery', dot: 'bg-status-progress', orders: d.outForDelivery },
    { key: 'done', title: 'Delivered today', dot: 'bg-status-success', orders: d.deliveredToday },
    ...d.upcoming.map((g) => ({ key: g.date || 'none', title: g.date ? expectedTitle(g.date) : 'No estimate yet', orders: g.orders })),
  ].filter((g) => g.orders.length > 0)
  let index = 0
  return (
    <div>
      {groups.map((g) => (
        <section key={g.key} aria-label={g.title}>
          <GroupLabel>
            {g.dot && <span className={cn('size-1.5 rounded-full', g.dot)} aria-hidden />}
            {g.title}
            <span className="tabular opacity-70">{count(g.orders.length)}</span>
          </GroupLabel>
          <ul>
            {g.orders.map((o) => (
              <li key={o.id} className="border-b">
                <PackageRow o={o} index={index++} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

function PackageRow({ o, index }: { o: Order; index: number }) {
  const item = o.items[0]
  const enter = rowEnter(index)
  return (
    // The item name is a stretched link, so the tracking button can sit in the row.
    <div style={enter.style} className={cn(rowClass, enter.className, COLS, 'relative py-3')}>
      <div className="flex min-w-0 items-center gap-3.5">
        <ProductThumb src={item?.imageUrl} retailer={o.retailer} className="size-10 rounded-lg text-xs" />
        <div className="min-w-0 space-y-0.5">
          <Link
            to="/orders/$orderNumber"
            params={{ orderNumber: o.orderNumber }}
            search={{ retailer: o.retailer }}
            className="block truncate text-sm font-medium after:absolute after:inset-0"
          >
            {item?.name ?? `Order ${o.orderNumber}`}
          </Link>
          <p className="text-muted-foreground flex items-center gap-1.5 text-[13px]">
            <RetailerMark retailer={o.retailer} className="size-4 rounded text-[7px]" />
            <span className="truncate">{o.email}</span>
          </p>
        </div>
      </div>

      <p className="text-muted-foreground hidden truncate text-[13px] lg:block">
        {o.address ? [o.address.recipientName, [o.address.city, o.address.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ') : '—'}
      </p>

      <div className="order-3 lg:order-none">
        <StatusBadge status={o.status} />
      </div>

      <div className="order-4 justify-self-end lg:order-none lg:justify-self-start">
        {o.trackingNumber ? (
          <Button asChild variant="outline" size="sm" className="relative z-10">
            <a href={trackingUrl(o.carrier, o.trackingNumber)} target="_blank" rel="noreferrer" aria-label={`Track with ${o.carrier ?? 'carrier'}`}>
              {o.carrier ?? 'Track'}
              <span className="id-mono text-subtle hidden xl:inline">…{o.trackingNumber.slice(-6)}</span>
              <ExternalLink className="size-3.5" />
            </a>
          </Button>
        ) : (
          <span className="text-subtle text-[13px]">No tracking yet</span>
        )}
      </div>
      <RowChevron />
    </div>
  )
}
